import { useRef, useState } from "react";
import { Check } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { getProfilePreferences } from "../../api/preferences";
import useClickOutside from "../../hooks/useClickOutside";
import useStyleSelectionStore from "../../store/styleSelectionStore";
import AnchoredPanel from "../shared/AnchoredPanel";
import CustomTooltip from "../Tooltip/CustomTooltip";

// Integral sign (∫) — not available in lucide-react.
const IntegralIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path transform="skewX(-14) translate(3,0)" d="M17 3c-.6-.6-1.4-1-2.3-1C13 2 12 3.4 12 5.6v12.8c0 2.2-1 3.6-2.7 3.6-.9 0-1.7-.4-2.3-1" />
  </svg>
);

const PromptStyleSelector = ({ value, onChange }) => {
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const storedSelection = useStyleSelectionStore((state) => state.selection);
  const setStoredSelection = useStyleSelectionStore((state) => state.setSelection);
  const selection = value || storedSelection;
  const setSelection = onChange || setStoredSelection;
  const { data: profile } = useQuery({
    queryKey: ["profile-preferences"],
    queryFn: getProfilePreferences,
    staleTime: 300000,
  });
  useClickOutside([anchorRef, panelRef], () => setOpen(false));

  const styles = profile?.styles || [];
  const sortedStyles = [...styles].sort((left, right) =>
    left.name.localeCompare(right.name, undefined, { sensitivity: "base" })
  );
  const activeStyle = styles.find((style) => style.style_id === profile?.active_style_id);
  const photonDefaultMode = !activeStyle && selection.mode === "none" ? "none" : "profile_default";
  const options = [
    ...(activeStyle ? [{
      key: "profile_default",
      label: activeStyle.name,
      description: "Profile default",
      selection: { mode: "profile_default" },
    }] : []),
    ...sortedStyles.filter((style) => style.style_id !== activeStyle?.style_id).map((style) => ({
      key: style.style_id,
      label: style.name,
      selection: { mode: "style", style_id: style.style_id },
    })),
    {
      key: activeStyle ? "none" : photonDefaultMode,
      label: "Photon default",
      description: activeStyle
        ? "Ignore the Profile style for this response"
        : "Use Photon without a personal style",
      selection: { mode: activeStyle ? "none" : photonDefaultMode },
    },
  ];
  const selectedKey = selection.mode === "style" ? selection.style_id : selection.mode;

  return (
    <>
      <CustomTooltip content="My Style" side="top" align="center">
        <button
          ref={anchorRef}
          type="button"
          onClick={() => setOpen((current) => !current)}
          aria-label="Choose My Style"
          className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${selection.mode === "style" ? "bg-fill-positive-surface text-ink-positive-ink dark:bg-fill-positive-surface-dark dark:text-ink-accent" : "bg-fill-navigation-hover text-ink-gray-700 dark:bg-fill-navigation-active dark:text-ink-gray-200"}`}
        >
          <IntegralIcon />
        </button>
      </CustomTooltip>
      <AnchoredPanel anchorRef={anchorRef} isOpen={open} onClose={() => setOpen(false)} width={240} className="max-h-64 overflow-y-auto">
        <div ref={panelRef} className="p-1">
          <div className="border-b border-edge-gray-200 px-3 py-2 text-xs font-semibold text-ink-gray-500 dark:border-edge-panel-border dark:text-ink-gray-400">My Style</div>
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => { setSelection(option.selection); setOpen(false); }}
              className="flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm text-ink-gray-700 hover:bg-fill-gray-100 dark:text-ink-gray-200 dark:hover:bg-fill-gray-700"
            >
              <span className="min-w-0 pr-2">
                <span className="block truncate">{option.label}</span>
                {option.description && <span className="mt-0.5 block text-[10px] leading-4 text-ink-gray-500 dark:text-ink-gray-400">{option.description}</span>}
              </span>
              {selectedKey === option.key && <Check className="h-4 w-4 text-ink-teal-600" />}
            </button>
          ))}
          <p className="px-3 py-2 text-[11px] leading-4 text-ink-gray-500 dark:text-ink-gray-400">Spectra instructions take precedence when they conflict.</p>
        </div>
      </AnchoredPanel>
    </>
  );
};

export default PromptStyleSelector;